/**
 * O áudio gravado vira WAV 16 kHz mono antes de ir para a transcrição.
 *
 * Cada navegador grava num formato (Chrome/Edge/Brave: webm/opus; Firefox:
 * ogg; Safari: mp4), e webm não está na lista oficial do Gemini. WAV está, e
 * 16 kHz mono é o que reconhecimento de fala usa: ~32 KB por segundo.
 */

export const TAXA_WAV = 16_000;

/** PCM 16 bits little-endian com o cabeçalho RIFF de 44 bytes. */
export function codificarWav(
  amostras: Float32Array,
  taxa = TAXA_WAV,
): ArrayBuffer {
  const dados = amostras.length * 2;
  const buffer = new ArrayBuffer(44 + dados);
  const v = new DataView(buffer);
  const texto = (pos: number, s: string) =>
    [...s].forEach((c, i) => v.setUint8(pos + i, c.charCodeAt(0)));
  texto(0, "RIFF");
  v.setUint32(4, 36 + dados, true);
  texto(8, "WAVE");
  texto(12, "fmt ");
  v.setUint32(16, 16, true); // tamanho do bloco fmt
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, taxa, true);
  v.setUint32(28, taxa * 2, true); // bytes por segundo
  v.setUint16(32, 2, true); // bytes por amostra
  v.setUint16(34, 16, true); // bits por amostra
  texto(36, "data");
  v.setUint32(40, dados, true);
  for (let i = 0; i < amostras.length; i++) {
    const s = Math.max(-1, Math.min(1, amostras[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

/** Qualquer áudio que o navegador saiba tocar → WAV 16 kHz mono. */
export async function paraWav(gravacao: Blob): Promise<Blob> {
  const ctx = new AudioContext();
  let audio: AudioBuffer;
  try {
    audio = await ctx.decodeAudioData(await gravacao.arrayBuffer());
  } finally {
    void ctx.close();
  }
  // O OfflineAudioContext de 1 canal já mistura para mono e reamostra.
  const offline = new OfflineAudioContext(
    1,
    Math.max(1, Math.ceil(audio.duration * TAXA_WAV)),
    TAXA_WAV,
  );
  const fonte = offline.createBufferSource();
  fonte.buffer = audio;
  fonte.connect(offline.destination);
  fonte.start();
  const mono = await offline.startRendering();
  return new Blob([codificarWav(mono.getChannelData(0))], {
    type: "audio/wav",
  });
}

/** Base64 sem o prefixo "data:...;base64,". */
export const paraBase64 = (blob: Blob): Promise<string> =>
  new Promise((ok, falha) => {
    const leitor = new FileReader();
    leitor.onload = () => ok(String(leitor.result).split(",")[1] ?? "");
    leitor.onerror = () => falha(leitor.error);
    leitor.readAsDataURL(blob);
  });
